import {
  PRICE_URL,
  PAYMENT_METHODS_URL,
  CREATE_BILL_URL,
  GET_BILLS_URL,
  CHECK_BILL_STATUS_URL,
} from "@/shared/api";
import { createRepository } from "@/shared/repositories/Repository";
import type {
  PaymentTariffsResponse,
  PaymentMethodsResponse,
  GetPaymentMethodsRequest,
  CreateBillRequest,
  CreateBillResponse,
  BillStatusResponse,
  BillsHistoryResponse,
} from "../model/types";

const paymentRepository = createRepository({
  basePath: PRICE_URL,
  useAuth: true,
});

const paymentMethodsRepository = createRepository({
  basePath: PAYMENT_METHODS_URL,
  useAuth: true,
});

const createBillRepository = createRepository({
  basePath: CREATE_BILL_URL,
  useAuth: true,
});

const billsHistoryRepository = createRepository({
  basePath: GET_BILLS_URL,
  useAuth: true,
});

const checkBillStatusRepository = createRepository({
  basePath: CHECK_BILL_STATUS_URL,
  useAuth: true,
});

export const getPrice = async (
  subscriptionID?: string | null,
  autopaymentEnabled: boolean = false,
): Promise<{ status: number; data: PaymentTariffsResponse }> => {
  const query = new URLSearchParams();
  query.set("autopayment_enabled", String(autopaymentEnabled));
  if (subscriptionID) {
    query.set("subscription_id", subscriptionID);
  }
  const queryParams = `?${query.toString()}`;

  const errors = {
    401: "Пользователь не авторизован",
    403: "Подписка не принадлежит пользователю",
    404: "Подписка не найдена",
    422: "Ошибка валидации данных",
  };

  return paymentRepository.get<PaymentTariffsResponse>(
    queryParams,
    undefined,
    errors,
  );
};

export const getPaymentMethods = async (
  params: GetPaymentMethodsRequest,
): Promise<{ status: number; data: PaymentMethodsResponse }> => {
  const query = new URLSearchParams();
  query.set("months", String(params.months));
  query.set("autopayment_enabled", String(params.autopayment_enabled));
  if (params.subscription_id) {
    query.set("subscription_id", params.subscription_id);
  }
  const queryParams = `?${query.toString()}`;

  const errors = {
    401: "Пользователь не авторизован",
    422: "Ошибка валидации данных",
  };

  return paymentMethodsRepository.get<PaymentMethodsResponse>(
    queryParams,
    undefined,
    errors,
  );
};

export const createBill = async (
  billData: CreateBillRequest,
): Promise<{ status: number; data: CreateBillResponse }> => {
  // Создание счета на оплату
  // Может вернуть либо success: true для оплаты с баланса, либо url для редиректа на платежную систему

  const errors = {
    400: "Ошибка в запросе (например, указаны некорректные данные)",
    401: "Пользователь не авторизован",
    403: "Подписка не принадлежит пользователю",
    404: "Подписка не найдена",
    422: "Ошибка валидации данных",
  };

  return createBillRepository.post<CreateBillResponse, CreateBillRequest>(
    "",
    billData,
    errors,
  );
};

export const getBillsHistory = async (): Promise<{
  status: number;
  data: BillsHistoryResponse;
}> => {
  // Получение истории транзакций пользователя
  // Возвращает массив транзакций с информацией об автоплатежах

  const errors = {
    401: "Пользователь не авторизован",
  };

  return billsHistoryRepository.get<BillsHistoryResponse>(
    "",
    undefined,
    errors,
  );
};
export const checkBillStatus = async (
  billId: string,
): Promise<{ status: number; data: BillStatusResponse }> => {
  // Проверка статуса оплаты счета
  // Возвращает {"paid": true} если оплата прошла успешно, и {"paid": false} в противном случае

  const errors = {
    401: "Пользователь не авторизован",
    403: "Счёт не принадлежит пользователю",
    404: "Счет не найден",
    422: "Ошибка валидации данных",
  };

  return checkBillStatusRepository.get<BillStatusResponse>(
    `/${billId}/status`,
    undefined,
    errors,
  );
};
